/**
 * Globe — R3F sphere driven by a uniform-based Earth shader.
 *
 * Slow auto-rotation, axial tilt, and a children slot for surface overlays
 * (PoP markers, regions, country borders). The shader is a stub: a fallback
 * dark-blue tint with a fresnel rim, ready to be swapped for a real Earth
 * material once textures are wired in P1.
 */

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef, type ReactNode } from 'react';
import {
  Color,
  DoubleSide,
  type Group,
  type Mesh,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from 'three';

export interface GlobeProps {
  /** Globe radius in scene units. Default 1. */
  readonly radius?: number;
  /** Axial tilt in degrees. Earth is 23.4. */
  readonly tiltDeg?: number;
  /** Rotation speed in radians/sec. Default 0.02. */
  readonly rotationSpeed?: number;
  /** Base surface tint of the Earth shader. Default dark blue. */
  readonly baseColor?: string | number;
  /** Fresnel rim color of the Earth shader. Default cyan. */
  readonly rimColor?: string | number;
  /** Children rendered as siblings (typically PoP markers, Arcs). */
  readonly children?: ReactNode;
}

const VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vNormal = normalize(mat3(modelMatrix) * normal);
    vViewDir = normalize(cameraPosition - worldPos.xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  uniform vec3 uBaseColor;
  uniform vec3 uRimColor;
  uniform float uTime;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec2 vUv;

  void main() {
    float fres = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), 2.0);
    vec3 base = uBaseColor * (0.35 + 0.15 * sin(uTime * 0.2 + vUv.y * 6.2831));
    vec3 rim = uRimColor * fres;
    gl_FragColor = vec4(base + rim, 1.0);
  }
`;

export function Globe({
  radius = 1,
  tiltDeg = 23.4,
  rotationSpeed = 0.02,
  baseColor = 0x0b1a2a,
  rimColor = 0x4ad7ff,
  children,
}: GlobeProps): JSX.Element {
  const groupRef = useRef<Group>(null);
  const meshRef = useRef<Mesh>(null);

  const geometry = useMemo(() => new SphereGeometry(radius, 96, 64), [radius]);

  const material = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        uniforms: {
          uTime: { value: 0 },
          uBaseColor: { value: new Color(baseColor) },
          uRimColor: { value: new Color(rimColor) },
        },
        side: DoubleSide,
      }),
    [baseColor, rimColor],
  );

  const tiltAxis = useMemo(() => new Vector3(0, 0, 1), []);

  useFrame((_, dt) => {
    material.uniforms.uTime!.value += dt;
    if (groupRef.current) {
      groupRef.current.setRotationFromAxisAngle(tiltAxis, (tiltDeg * Math.PI) / 180);
    }
    if (meshRef.current) {
      meshRef.current.rotation.y += rotationSpeed * dt;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={meshRef} geometry={geometry} material={material} />
      {children}
    </group>
  );
}
